import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { services } from '../data/mock';

const ServicesPage = () => {
  return (
    <div className="bg-[#F4EFE8] min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] lg:h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1602028915047-37269d1a73f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxtb2R1bGFyJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NzEyMzgwNzN8MA&ixlib=rb-4.1.0&q=85"
            alt="Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#111111]/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Our Services
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Precision engineering meets luxury design. Every detail crafted to perfection.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-16 lg:space-y-24">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="aspect-[4/3] overflow-hidden bg-white shadow-md">
                    <img
                      src={
                        service.id === 1
                          ? 'https://images.unsplash.com/photo-1696986681606-b156ccd761c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBraXRjaGVufGVufDB8fHx8MTc3MTIzODA2NXww&ixlib=rb-4.1.0&q=85'
                          : service.id === 2
                          ? 'https://images.unsplash.com/photo-1672137233327-37b0c1049e77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJkcm9iZXxlbnwwfHx8fDE3NzEyMzgxMDJ8MA&ixlib=rb-4.1.0&q=85'
                          : service.id === 3
                          ? 'https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBraXRjaGVufGVufDB8fHx8MTc3MTIzODEyMHww&ixlib=rb-4.1.0&q=85'
                          : 'https://images.unsplash.com/photo-1765277789187-17428ac17008?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTk0MzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdG9yYWdlJTIwdW5pdCUyMGludGVyaW9yfGVufDB8fHx8MTc3MTIzODEyNnww&ixlib=rb-4.1.0&q=85'
                      }
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl text-[#111111] mb-6">
                    {service.title}
                  </h2>
                  <p className="text-[#242424] text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <ul className="space-y-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 size={20} className="text-[#B89A4F] flex-shrink-0 mt-1" />
                        <span className="text-[#242424]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-4">
              Premium Materials
            </h2>
            <p className="text-[#242424] max-w-2xl mx-auto leading-relaxed">
              We use only the finest materials to ensure durability, aesthetics, and lasting value.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: 'Marine-grade Plywood', desc: 'Water-resistant & durable core construction' },
              { name: 'High-density MDF', desc: 'High-density moisture-resistant structural boards' },
              { name: 'Premium German Hardware', desc: 'Soft-close mechanisms & anti-rust fittings' },
              { name: 'German Hardware Systems', desc: 'Precision-engineered smooth operation' },
              { name: 'Matte Acrylic', desc: 'Scratch-resistant luxury finishes' },
              { name: 'Quartz & Granite Countertops', desc: 'Heat & stain-resistant surfaces' }
            ].map((material, idx) => (
              <div key={idx} className="bg-[#F4EFE8] p-8 rounded-luxury">
                <h3 className="text-xl font-semibold text-[#111111] mb-3">{material.name}</h3>
                <p className="text-[#242424] text-sm leading-relaxed">{material.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-[#111111] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl mb-6">
            Let's Discuss Your Project
          </h2>
          <p className="text-lg text-white/80 mb-12 leading-relaxed">
            Get in touch to explore how we can bring your vision to life with precision and care.
          </p>
          <a
            href="/contact"
            className="inline-block bg-[#111111] text-[#B89A4F] px-12 py-5 text-sm tracking-widest uppercase hover:bg-[#B89A4F] hover:text-[#111111] transition-all duration-300 rounded-luxury"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
